import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, EMPTY, Observable, Subscription, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AutorizacaoManualModel, toAutorizacaoManualModel } from '../../model/autorizacao-manual.model';
import { LoginModel } from '../../model/login.model';
import {
  extrairMensagemSwapAutorizacaoManual,
  isSwapAutorizacaoManualOk,
  SwapCombustivel,
  SwapGravarSessaoRequest,
  SwapGravarSessaoResponse,
} from '../../model/swap';
import { SwapService } from '../../services/swap.service';
import { FlashMessageService } from '../../services/flash-message.service';
import { AuthenticationService } from '../../services/authentication.service';
import { SnackBarService } from '../../services/snack-bar-service.service';
import { PesquisaMotoristaAutorizacaoManualComponent } from '../../motorista/pesquisa-motorista-autorizacao-manual/pesquisa-motorista-autorizacao-manual.component';
import { IMotoristaModel } from '../../motorista/model/motorista.model';
import { criarFiltroInicialPesquisaMotoristaAutorizacaoManual } from '../../motorista/pesquisa-motorista-autorizacao-manual/motorista-pesquisa-autorizacao-manual.config';

export type ModoCadastroSimples = 'incluir' | 'repetir';

/** Máximo de tentativas para contornar `proximaEtapa === 'SESSAO_EXISTENTE'` (codRetorno '0409'). */
const MAX_TENTATIVAS_SESSAO_DUPLICADA = 3;

/**
 * Tetos para os campos numéricos enviados em `Swap/GravarSessao`.
 * Refletem (ou são levemente mais conservadores que) os tipos do backend
 * — protegem contra overflow de `int` (Int32) e estouro de `decimal`
 * (procedure `PR_SWAP_INSERE_PREAUTORIZACAO_PORTAL`).
 *
 *  - PRECO_UNITARIO_MAX   → backend `decimal(7,2)`  (99.999,99)
 *  - LITROS_MAX           → backend `decimal(12,2)` (limitamos a 99.999,99 por sanidade)
 *  - VALOR_TOTAL_MAX      → backend `decimal(18,2)` (limitamos a 9.999.999,99 por sanidade)
 *  - HODOMETRO_MAX        → backend `int` (Int32). Limitamos a 9.999.999 (7 dígitos)
 *                            que é o teto realista de hodômetros físicos.
 *  - COD_MOTORISTA_MAX    → backend `int` (Int32). Validação defensiva: o
 *                            valor vem da pesquisa, mas garantimos o range.
 */
const LIMITES = {
  PRECO_UNITARIO_MAX: 99_999.99,
  LITROS_MAX: 99_999.99,
  VALOR_TOTAL_MAX: 9_999_999.99,
  HODOMETRO_MAX: 9_999_999,
  COD_MOTORISTA_MAX: 2_147_483_647,
} as const;

/**
 * Cadastro de autorização manual (modo `incluir` ou `repetir`).
 *
 * Fluxo de submissão (Confirmar):
 *  1. Gera novo `dscSessionExterna` (UUID) e monta o request com
 *     `bypass = this.bypassMarcado` (vai no body).
 *  2. `swapService.gravarSessao(...)` — retenta até 3x se vier
 *     `proximaEtapa === 'SESSAO_EXISTENTE'`. O backend já aplica o
 *     bypass na mesma chamada quando `request.bypass === true`.
 *  3. `swapService.incluirAutorizacaoManual(...)` — efetiva o pedido na BIS-SWAP.
 *     Erros de negócio (`statusRetorno === 'NEGADO' | 'ERRO'`) chegam
 *     como HTTP 200; o `.subscribe()` distingue via `isSwapAutorizacaoManualOk`
 *     e mostra a mensagem com `extrairMensagemSwapAutorizacaoManual`.
 *
 * Tratamento da resposta de gravação:
 *  - Sem bypass: 1 flash (sucesso ou erro).
 *  - Com bypass + sucesso: 2 flashes verdes (gravação + bypass).
 *  - Com bypass + falha do bypass: 1 flash verde (gravação) + 1
 *    flash amarelo com `bypass.dscRetorno` (a sessão foi gravada
 *    mas o bypass não foi aplicado; a gravação NÃO é desfeita).
 *  - Erro de gravação: 1 flash vermelho — o backend não chega a
 *    aplicar bypass se a gravação falhar.
 *
 * Em qualquer falha intermediária a operação é interrompida e a
 * mensagem é exibida via FlashMessage.
 *
 * Lista de combustíveis (`combustiveis`):
 *  - É carregada dinamicamente em função da placa via
 *    `swapService.buscarCombustiveis(guid, placa)`.
 *  - No modo `incluir`, os campos numéricos começam vazios; exemplos
 *    aparecem só como `placeholder`. A combo de combustível começa vazia
 *    e é populada quando o usuário sai do campo placa (`(blur)` ->
 *    `onPlacaBlur`).
 *  - No modo `repetir`, a combo é populada com base na placa carregada
 *    da sessão de origem; o `cdProduto` original é preservado se ainda
 *    estiver na lista do veículo, e zerado caso contrário.
 *  - Se a placa for apagada, a combo é esvaziada silenciosamente.
 *  - Erros de negócio (`Sucesso=false`) são exibidos como aviso amarelo
 *    com a mensagem retornada pelo backend.
 */
@Component({
  selector: 'app-autorizacao-manual-cadastro',
  templateUrl: './autorizacao-manual-cadastro.component.html',
  styleUrls: ['./autorizacao-manual-cadastro.component.scss'],
})
export class AutorizacaoManualCadastroComponent implements OnInit, OnDestroy {
  get tituloLocal(): string {
    return this.modo === 'repetir'
      ? 'Autorização Manual / Repetir solicitação'
      : 'Autorização Manual / Inclusão';
  }

  /** Dados do usuário logado para aplicar aparência/tema. */
  usuario: LoginModel | undefined;

  /** Controla a exibição do spinner de loading. */
  loading = 0;

  modo: ModoCadastroSimples = 'incluir';

  /** URL absoluta da lista a abrir após salvar/cancelar/erro. */
  voltarLista = '/autorizacao-manual';

  /** `dscSessionExterna` do registro carregado (somente em `repetir`). */
  dscSessionExternaOrigem: string | null = null;

  /** Estado do formulário em modelo de UI. */
  modelo: Partial<AutorizacaoManualModel> = {};

  /** Evita mostrar campos vazios enquanto `obterSessao` está em curso no modo repetir. */
  carregandoForm = false;

  /** Indica se o bypass deve ser aplicado na inclusão/repetição. */
  bypassMarcado = false;

  /** Lista de combustíveis para o select. */
  combustiveis: SwapCombustivel[] = [];

  /** Tetos numéricos expostos para o template (atributos `[max]`). */
  readonly LIMITES = LIMITES;

  /** Placeholders do modo inclusão (valores sugeridos; campos numéricos começam vazios). */
  readonly placeholdersIncluir = {
    placa: 'ABC1D23',
    preco: 'Ex.: 5,89',
    valorTotal: 'Ex.: 350,50',
    litros: 'Ex.: 59,5',
    hodometro: 'Ex.: 125000',
  } as const;

  private sub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private swapService: SwapService,
    private flash: FlashMessageService,
    private authService: AuthenticationService,
    private snackBar: SnackBarService,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.loading++;
    this.usuario = this.authService.getDadosUserLocal();

    this.authService
      .validate(this.usuario?.retorno?.guid ?? '')
      .then((dados) => {
        if (!dados.sucesso) {
          this.snackBar.flashMessage(
            this.authService.msgSessaoExpirada,
            ['snackbar-error'],
            'Fechar',
            0,
          );
          this.usuario = undefined;
        } else {
          this.iniciar();
        }
      })
      .catch(() => {
        this.snackBar.flashMessage(
          this.authService.msgSessaoExpirada,
          ['snackbar-error'],
          'Fechar',
          0,
        );
      })
      .finally(() => {
        this.loading--;
      });
  }

  private iniciar(): void {
    this.renderer.addClass(
      document.body,
      this.usuario?.retorno?.aparencia ?? 'PADRAO',
    );
    this.sub = combineLatest([this.route.data, this.route.paramMap]).subscribe(
      ([data, params]) => {
        this.modo = (data['modo'] as ModoCadastroSimples) ?? 'incluir';
        const v = data['voltarLista'];
        this.voltarLista =
          typeof v === 'string' && v.length > 0 ? v : '/autorizacao-manual';

        this.dscSessionExternaOrigem =
          this.modo === 'repetir' ? params.get('dscSessionExterna') : null;

        this.flash.limpar();
        this.bypassMarcado = false;
        this.iniciarFormulario();
      },
    );
  }

  /**
   * Carrega os combustíveis elegíveis para a placa informada.
   *
   * Comportamento:
   *  - Placa vazia/inválida → limpa a combo e o `cdProduto` selecionado,
   *    sem flash (não há erro a comunicar).
   *  - Sucesso → ordena por código e filtra descrições vazias. Se o
   *    `cdProduto` previamente selecionado não estiver na nova lista,
   *    é zerado para forçar nova escolha consciente do usuário.
   *  - `Sucesso=false` (erro de negócio HTTP 200) → exibe a mensagem
   *    do backend como aviso amarelo e zera a combo.
   *  - HTTP error → exibe flash vermelho genérico e zera a combo.
   */
  private carregarCombustiveisPorPlaca(placa: string): void {
    const guid = this.usuario?.retorno?.guid ?? '';
    const placaNorm = (placa ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

    if (!guid || !placaNorm) {
      this.combustiveis = [];
      this.modelo.cdProduto = '';
      return;
    }

    this.loading++;
    this.swapService.buscarCombustiveis(guid, placaNorm).subscribe({
      next: (res) => {
        this.loading--;
        if (res.sucesso && res.retorno) {
          this.combustiveis = [...res.retorno]
            .filter((c) => c.dscCombustivel?.trim())
            .sort((a, b) => a.codCombustivel - b.codCombustivel);

          const cdAtual = this.modelo.cdProduto;
          if (cdAtual) {
            const aindaExiste = this.combustiveis.some(
              (c) => c.codCombustivel.toString() === cdAtual,
            );
            if (!aindaExiste) {
              this.modelo.cdProduto = '';
            }
          }
          return;
        }
        this.combustiveis = [];
        this.modelo.cdProduto = '';
        const msg =
          res.mensagens?.[0] ??
          'Não foi possível obter os combustíveis para esta placa.';
        this.flash.aviso(msg);
      },
      error: (err) => {
        this.loading--;
        this.combustiveis = [];
        this.modelo.cdProduto = '';
        console.error('Erro ao carregar combustíveis:', err);
        this.flash.erro('Erro ao carregar os combustíveis.');
      },
    });
  }

  /**
   * Handler do `(blur)` do campo placa: dispara o carregamento
   * dos combustíveis elegíveis para o veículo.
   */
  onPlacaBlur(): void {
    this.carregarCombustiveisPorPlaca(this.modelo.placa ?? '');
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  voltar(): void {
    this.flash.limpar();
    void this.router.navigateByUrl(this.voltarLista);
  }

  abrirBuscaMotorista(): void {
    const filtroMotorista = criarFiltroInicialPesquisaMotoristaAutorizacaoManual(
      this.usuario?.retorno?.guid ?? '',
    );
    const dialogRef = this.dialog.open(PesquisaMotoristaAutorizacaoManualComponent, {
      width: '800px',
      height: '700px',
      data: { dataKey: filtroMotorista },
    });
    dialogRef.afterClosed().subscribe((response) => {
      if (!response?.data) {
        return;
      }
      const m = response.data as IMotoristaModel;

      this.modelo.codMotorista =
        typeof m.codigo === 'number' ? m.codigo : Number(m.codigo) || null;
      this.modelo.nomeMotorista = m.nome || m.apelido || '';
      this.modelo.cpfMotorista = (m.cpf || '').replace(/\D/g, '');

      // Regra: prioriza DDD_CELULAR + CELULAR; se faltar algum, tenta
      // DDD_TELEFONE + TELEFONE; se ainda assim faltar, deixa vazio.
      // Mantém apenas dígitos — a máscara é aplicada no template e
      // alterna entre 10 e 11 dígitos via `mascaraTelefone`.
      const dddCel = (m.numDddCelular || '').replace(/\D/g, '');
      const numCel = (m.numCelular || '').replace(/\D/g, '');
      const dddTel = (m.numDddTelefone || '').replace(/\D/g, '');
      const numTel = (m.numTelefone || '').replace(/\D/g, '');

      if (dddCel && numCel) {
        this.modelo.celularMotorista = `${dddCel}${numCel}`;
      } else if (dddTel && numTel) {
        this.modelo.celularMotorista = `${dddTel}${numTel}`;
      } else {
        this.modelo.celularMotorista = '';
      }
    });
  }

  limparMotorista(): void {
    this.modelo.codMotorista = null;
    this.modelo.nomeMotorista = '';
    this.modelo.cpfMotorista = '';
    this.modelo.celularMotorista = '';
  }

  limparCampoPlacaForm(): void {
    this.modelo.placa = '';
    this.carregarCombustiveisPorPlaca('');
  }

  limparCombustivelSelecionado(): void {
    this.modelo.cdProduto = '';
  }

  limparPrecoUnitarioCampo(): void {
    this.modelo.precoUnitario = undefined;
    this.recalcularValorTotal();
  }

  limparLitrosCampo(): void {
    this.modelo.litros = undefined;
    this.recalcularValorTotal();
  }

  limparValorTotalCampo(): void {
    this.modelo.valorTotal = undefined;
  }

  limparHodometroCampo(): void {
    this.modelo.hodometro = undefined;
  }

  /** Indica se há valor digitado em campo numérico (inclui `0`). */
  campoNumeroComValor(val: unknown): boolean {
    if (val == null || val === '') {
      return false;
    }
    return Number.isFinite(Number(val));
  }

  /**
   * Máscara dinâmica para o campo de telefone/celular do motorista.
   * 11 dígitos → celular `(00) 00000-0000`; caso contrário (10 ou
   * número incompleto) → fixo `(00) 0000-0000`. O critério é o
   * número de dígitos do valor já armazenado em `celularMotorista`
   * (que sempre vem só com dígitos do `abrirBuscaMotorista`).
   */
  get mascaraTelefone(): string {
    const digitos = (this.modelo.celularMotorista ?? '').replace(/\D/g, '');
    return digitos.length === 11 ? '(00) 00000-0000' : '(00) 0000-0000';
  }

  /**
   * Recalcula `valorTotal` a partir de `precoUnitario * litros`.
   * Disparado no `(blur)` dos campos litros e preço unitário.
   */
  recalcularValorTotal(): void {
    const preco =
      this.modelo.precoUnitario == null ? NaN : Number(this.modelo.precoUnitario);
    const litros =
      this.modelo.litros == null ? NaN : Number(this.modelo.litros);
    if (Number.isFinite(preco) && Number.isFinite(litros) && preco > 0 && litros > 0) {
      this.modelo.valorTotal = +(preco * litros).toFixed(2);
    }
  }

  /**
   * Recalcula `litros` a partir de `valorTotal / precoUnitario`.
   * Disparado no `(blur)` do campo valor total.
   *
   * Se o preço unitário não estiver preenchido, mostra aviso e
   * limpa o valor total (não dá para calcular).
   */
  recalcularLitros(): void {
    const preco =
      this.modelo.precoUnitario == null ? NaN : Number(this.modelo.precoUnitario);
    const total =
      this.modelo.valorTotal == null ? NaN : Number(this.modelo.valorTotal);
    if (!Number.isFinite(preco) || preco <= 0) {
      this.flash.aviso('Informe o "Valor por Litro" antes de alterar o "Valor Total".');
      this.modelo.valorTotal = undefined;
      return;
    }
    if (Number.isFinite(total) && total > 0) {
      this.modelo.litros = +(total / preco).toFixed(2);
    }
  }

  confirmar(): void {
    const erro = this.validar();
    if (erro) {
      this.flash.aviso(erro);
      return;
    }

    const guidUsuario = this.usuario?.retorno?.guid ?? '';
    const requestBase = this.montarRequestBase();

    this.loading++;
    this.gravarComRetry(guidUsuario, requestBase, MAX_TENTATIVAS_SESSAO_DUPLICADA)
      .pipe(
        switchMap((respostaGravacao) => {
          this.publicarFlashesPosGravacao(respostaGravacao);
          const sessionExterna = respostaGravacao.sessionExterna ?? '';
          return this.swapService.incluirAutorizacaoManual(guidUsuario, { sessionExterna });
        }),
        catchError((err) => {
          this.loading--;
          this.flash.erro(
            this.formatarErro(
              err,
              'Sessão gravada, mas falha ao efetivar a autorização manual.',
            ),
          );
          return EMPTY;
        }),
      )
      .subscribe((resp) => {
        this.loading--;
        if (isSwapAutorizacaoManualOk(resp)) {
          this.flash.sucesso('Autorização manual enviada.');
          void this.router.navigateByUrl(this.voltarLista);
          return;
        }
        // HTTP 200 + statusRetorno NEGADO/ERRO (ou OK com flgAutorizado != 'S'):
        // erro de negócio, NÃO cai no catchError — exibe a mensagem retornada
        // pela BIS-SWAP e mantém o usuário na tela.
        this.flash.erro(
          extrairMensagemSwapAutorizacaoManual(resp) ??
            'Autorização manual não foi efetivada.',
        );
      });
  }

  /**
   * Publica 1 ou 2 flash messages com base na resposta da gravação.
   *
   * Esta etapa cobre APENAS a gravação da sessão. A confirmação final
   * ("Autorização manual enviada.") é emitida no ramo de sucesso do
   * `.subscribe` em `confirmar()`, depois que o `incluirAutorizacaoManual` retorna
   * `OK + flgAutorizado='S'`. Por isso a mensagem aqui é deliberadamente
   * neutra — se o `incluirAutorizacaoManual` falhar (erro de negócio NEGADO/ERRO ou
   * erro técnico), o usuário verá o flash verde da gravação seguido do
   * flash vermelho do erro, sem mensagens contraditórias do tipo
   * "registrada com sucesso" + "não foi efetivada".
   *
   *  - Sempre: 1 flash verde com a mensagem de sucesso da gravação.
   *  - Se `bypass != null` (foi solicitado):
   *      - `bypass.flgSucesso === true`  → +1 flash verde.
   *      - `bypass.flgSucesso === false` → +1 flash amarelo (aviso).
   */
  private publicarFlashesPosGravacao(resp: SwapGravarSessaoResponse): void {
    const msgGravacao =
      this.modo === 'repetir'
        ? 'Solicitação registrada (repetição).'
        : 'Sessão gravada com sucesso.';
    this.flash.sucesso(msgGravacao);

    if (resp.bypass) {
      const texto = resp.bypass.dscRetorno?.trim();
      if (resp.bypass.flgSucesso) {
        this.flash.sucesso(texto || 'Bypass aplicado.');
      } else {
        this.flash.aviso(texto || 'Bypass não aplicado.');
      }
    }
  }

  /**
   * Tenta `gravarSessao` até `tentativas` vezes, gerando novo
   * `dscSessionExterna` quando vier `proximaEtapa === 'SESSAO_EXISTENTE'`.
   * O backend só aplica bypass quando a gravação ocorre de fato — ou
   * seja, em `SESSAO_EXISTENTE` o bypass NÃO é aplicado nessa
   * tentativa, garantindo que não há risco de bypass duplicado.
   */
  private gravarComRetry(
    guidUsuario: string,
    requestBase: Omit<SwapGravarSessaoRequest, 'dscSessionExterna'>,
    tentativas: number,
  ): Observable<SwapGravarSessaoResponse> {
    const request: SwapGravarSessaoRequest = {
      ...requestBase,
      dscSessionExterna: this.gerarUuid(),
    };
    return this.swapService.gravarSessao(guidUsuario, request).pipe(
      switchMap((res) => {
        if (res?.proximaEtapa === 'SESSAO_EXISTENTE') {
          if (tentativas > 1) {
            return this.gravarComRetry(guidUsuario, requestBase, tentativas - 1);
          }
          return throwError(
            () =>
              new Error(
                'Não foi possível gerar uma sessão única. Tente novamente.',
              ),
          );
        }
        return [res];
      }),
      catchError((err) => {
        // Erros HTTP (0422/0500) ou esgotamento de retentativas.
        this.loading--;
        this.flash.erro(this.formatarErro(err, 'Falha ao gravar a sessão.'));
        return EMPTY;
      }),
    );
  }

  /**
   * Valida o formulário antes de submeter.
   *
   * Para cada campo numérico:
   *  1. `Number()` converte o `ngModel` (string vinda do `<input type="number">`)
   *     em número; entradas com letras viram `NaN` e são rejeitadas pelo
   *     `Number.isFinite`.
   *  2. Verifica positividade.
   *  3. Verifica teto (ver constante `LIMITES`) — protege contra overflow
   *     de `int` (Int32) e estouro de `decimal` no banco. Sem esse teto,
   *     o backend devolve um 400 com mensagem técnica e o usuário vê
   *     "Http failure response..." no front (caso típico do `NUM_KM`).
   *  4. Para campos `int` no backend (hodômetro, codMotorista), exige
   *     também `Number.isInteger`.
   */
  private validar(): string | null {
    if (!this.modelo.codMotorista) {
      return 'Selecione um motorista (clique na lupa).';
    }
    const codMot = Number(this.modelo.codMotorista);
    if (
      !Number.isFinite(codMot) ||
      !Number.isInteger(codMot) ||
      codMot <= 0 ||
      codMot > LIMITES.COD_MOTORISTA_MAX
    ) {
      return 'Motorista inválido. Refaça a busca pela lupa.';
    }
    if (!this.modelo.placa?.trim()) {
      return 'Informe a placa.';
    }
    if (!this.modelo.cdProduto?.trim()) {
      return 'Selecione o combustível.';
    }

    const preco =
      this.modelo.precoUnitario == null ? NaN : Number(this.modelo.precoUnitario);
    if (!Number.isFinite(preco) || preco <= 0) {
      return 'Informe um valor numérico válido para "Valor por Litro".';
    }
    if (preco > LIMITES.PRECO_UNITARIO_MAX) {
      return `O "Valor por Litro" não pode ser maior que ${this.formatarMoeda(
        LIMITES.PRECO_UNITARIO_MAX,
      )}.`;
    }

    const total =
      this.modelo.valorTotal == null ? NaN : Number(this.modelo.valorTotal);
    const litros =
      this.modelo.litros == null ? NaN : Number(this.modelo.litros);
    const temTotal = Number.isFinite(total) && total > 0;
    const temLitros = Number.isFinite(litros) && litros > 0;
    if (!temTotal && !temLitros) {
      return 'Informe o "Valor Total" ou os "Litros".';
    }
    if (temTotal && total > LIMITES.VALOR_TOTAL_MAX) {
      return `O "Valor Total" não pode ser maior que ${this.formatarMoeda(
        LIMITES.VALOR_TOTAL_MAX,
      )}.`;
    }
    if (temLitros && litros > LIMITES.LITROS_MAX) {
      return `O campo "Litros" não pode ser maior que ${this.formatarNumero(
        LIMITES.LITROS_MAX,
      )}.`;
    }

    const hodoRaw =
      this.modelo.hodometro == null ? NaN : Number(this.modelo.hodometro);
    if (!Number.isFinite(hodoRaw) || hodoRaw <= 0) {
      return 'Informe um valor numérico válido para "Hodômetro (KM)".';
    }
    const hodo = Math.trunc(hodoRaw);
    if (hodo <= 0) {
      return 'Informe um valor inteiro positivo para "Hodômetro (KM)".';
    }
    if (hodo > LIMITES.HODOMETRO_MAX) {
      return `O "Hodômetro (KM)" não pode ser maior que ${this.formatarNumero(
        LIMITES.HODOMETRO_MAX,
      )}.`;
    }

    return null;
  }

  private formatarNumero(n: number): string {
    return n.toLocaleString('pt-BR');
  }

  private formatarMoeda(n: number): string {
    return n.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  private montarRequestBase(): Omit<SwapGravarSessaoRequest, 'dscSessionExterna'> {
    return {
      codMotorista: Number(this.modelo.codMotorista ?? 0),
      pawPlaca: (this.modelo.placa ?? '').toUpperCase().replace(/[^A-Z0-9]/g, ''),
      vlrPreco: Number(this.modelo.precoUnitario ?? 0),
      qtdVolume: Number(this.modelo.litros ?? 0),
      vlrTotal: Number(this.modelo.valorTotal ?? 0),
      numKm: Math.trunc(Number(this.modelo.hodometro ?? 0)),
      cdProduto: this.modelo.cdProduto || undefined,
      bypass: this.bypassMarcado,
    };
  }

  private gerarUuid(): string {
    const c =
      typeof crypto !== 'undefined'
        ? (crypto as Crypto & { randomUUID?: () => string })
        : undefined;
    if (c?.randomUUID) {
      return c.randomUUID();
    }
    // Fallback (RFC4122 v4 simplificado).
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      const v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Extrai uma mensagem amigável de um erro HTTP do `HttpClient`.
   *
   * O backend `BIS-ctf-services` pode responder erros (BadRequest/422/500)
   * de várias formas, dependendo do controller:
   *  - **string**: ex. `BadRequest("Sessao invalida ou expirada.")`
   *  - **array de strings**: ex. `BadRequest(processamento.Mensagens)` —
   *    é o caso de `Swap/GravarSessao` quando a procedure rejeita
   *    (`["NUM_KM inválido. Informe um valor inteiro positivo."]`).
   *  - **objeto** com `dscRetorno`/`message`/`title` (ProblemDetails).
   *
   * Sem esse tratamento, mensagens vindas como array caíam no fallback
   * `e.message` do `HttpClient`, que é o texto técnico
   * `"Http failure response for ..."`.
   */
  private formatarErro(err: unknown, fallback: string): string {
    const e = err as { error?: unknown; message?: string };
    const body = e?.error;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (Array.isArray(body) && body.length) {
      const msgs = body
        .map((m) =>
          typeof m === 'string' ? m : (m as { toString?: () => string })?.toString?.() ?? '',
        )
        .filter(Boolean);
      if (msgs.length) {
        return msgs.join(' | ');
      }
    }

    if (body && typeof body === 'object') {
      const obj = body as {
        dscRetorno?: string;
        message?: string;
        title?: string;
      };
      if (obj.dscRetorno) {
        return obj.dscRetorno;
      }
      if (obj.message) {
        return obj.message;
      }
      if (obj.title) {
        return obj.title;
      }
    }

    return e?.message || fallback;
  }

  private iniciarFormulario(): void {
    if (this.modo === 'incluir') {
      this.preencherDefaultsInclusao();
      return;
    }

    if (this.modo === 'repetir' && this.dscSessionExternaOrigem) {
      this.carregandoForm = true;
      const guid = this.usuario?.retorno?.guid ?? '';
      this.swapService.obterSessao(guid, this.dscSessionExternaOrigem).subscribe({
        next: (res) => {
          this.carregandoForm = false;
          if (!res?.sucesso || !res.retorno) {
            this.flash.erro('Registro não encontrado.');
            void this.router.navigateByUrl(this.voltarLista);
            return;
          }
          const ui = toAutorizacaoManualModel(res.retorno);
          // Repetir = nova inclusão pré-preenchida; descarta identificadores da origem.
          this.modelo = {
            codMotorista: ui.codMotorista,
            nomeMotorista: ui.nomeMotorista,
            cpfMotorista: ui.cpfMotorista,
            celularMotorista: ui.celularMotorista,
            placa: ui.placa,
            cdProduto: ui.cdProduto,
            combustivel: ui.combustivel,
            precoUnitario: ui.precoUnitario,
            litros: ui.litros,
            valorTotal: ui.valorTotal,
            hodometro: ui.hodometro,
            codPosto: ui.codPosto,
            nomePosto: ui.nomePosto,
          };
          this.bypassMarcado = false;
          // Carrega a combo de combustíveis elegíveis para a placa
          // herdada da sessão de origem. O `cdProduto` original é
          // preservado pelo método se ainda estiver na nova lista.
          this.carregarCombustiveisPorPlaca(ui.placa ?? '');
        },
        error: () => {
          this.carregandoForm = false;
          this.flash.erro('Erro ao carregar registro.');
          void this.router.navigateByUrl(this.voltarLista);
        },
      });
      return;
    }

    // Repetir sem chave válida: cai para inclusão limpa.
    this.preencherDefaultsInclusao();
  }

  private preencherDefaultsInclusao(): void {
    this.combustiveis = [];
    this.modelo = {
      codMotorista: null,
      nomeMotorista: '',
      cpfMotorista: '',
      celularMotorista: '',
      placa: '',
      cdProduto: '',
      combustivel: '',
    };
  }
}
